import { Avatar, avatarLetter, Badge, Button, TextField } from '@eo/shared';
import { Mail } from 'lucide-react';
import { useState } from 'react';

import type { BasicInformationCardProps } from './types';

export function BasicInformationCard({ me, onSaveName }: BasicInformationCardProps) {
  const [name, setName] = useState(me.name);
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const dirty = trimmed !== me.name;

  const save = async () => {
    setSaving(true);
    try {
      await onSaveName(trimmed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-1 flex-col items-center gap-6 rounded-16 border border-border-faint p-6 sm:items-start sm:p-8 xl:flex-row xl:items-start">
      <Avatar
        size="xl"
        letter={avatarLetter(me.name, me.email)}
        imageUrl={me.avatarUrl}
        alt="Profile photo"
      />

      <div className="flex w-full min-w-0 flex-col gap-6 sm:gap-12 xl:flex-1">
        <div className="flex w-full flex-col gap-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl text-text-primary">Basic Information</h2>
            {me.isAdmin ? <Badge variant="secondary">Admin</Badge> : null}
          </div>

          <TextField
            id="display-name"
            label="Display Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={dirty && trimmed.length === 0}
            helperText={dirty && trimmed.length === 0 ? 'Enter a display name.' : undefined}
          />

          <TextField
            id="email"
            label="Email"
            value={me.email}
            readOnly
            leadingIcon={<Mail className="size-5" />}
            helperText="Your sign-in address. Ask an admin if this needs to change."
          />
        </div>

        <div className="flex w-full items-center gap-4 sm:w-auto sm:self-end">
          <Button
            variant="secondary"
            className="flex-1 sm:flex-initial"
            disabled={!dirty || saving}
            onClick={() => setName(me.name)}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 sm:flex-initial"
            disabled={!dirty || saving || trimmed.length === 0}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </section>
  );
}
